import React, { useState, useRef, useEffect, useCallback } from 'react';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// ── Autocomplete Nominatim (OpenStreetMap) — 100% gratuit, aucune clé API ──
function useNominatim(query, onResults) {
  useEffect(() => {
    if (!query || query.length < 2) { onResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=6&addressdetails=1`,
          { headers: { 'Accept-Language': 'fr' } }
        );
        const data = await res.json();
        const suggestions = data
          .filter(d => d.class === 'place' || d.class === 'boundary' || d.addresstype === 'city' || d.addresstype === 'town' || d.addresstype === 'village' || d.addresstype === 'municipality' || d.addresstype === 'administrative' || d.type === 'administrative' || d.type === 'city' || d.type === 'town' || d.type === 'village')
          .map(d => ({
            cityName: d.address?.city || d.address?.town || d.address?.village || d.address?.municipality || d.address?.hamlet || d.name || '',
            label: d.display_name,
            lat: parseFloat(d.lat),
            lng: parseFloat(d.lon),
          }))
          .filter((s, i, arr) => s.cityName && arr.findIndex(x => x.cityName === s.cityName) === i)
          .slice(0, 5);
        onResults(suggestions);
      } catch(e) {
        onResults([]);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);
}

function CitySuggestions({ suggestions, onSelect, style = {} }) {
  if (!suggestions.length) return null;
  return (
    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 99999, ...style }}>
      {suggestions.map((s, i) => (
        <div key={i} onClick={() => onSelect(s)}
          style={{ padding: '10px 16px', fontSize: 14, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <div>
            <div style={{ fontWeight: 500 }}>{s.cityName}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 1 }}>{s.label.split(',').slice(1, 3).join(',').trim()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────

function CalendarDouble({ arrival, departure, onChange, isMobile }) {
  const [month, setMonth] = useState(new Date());
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const nextMonth = new Date(year, monthIdx + 1);

  const renderMonth = (y, m) => {
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    return Array(offset).fill(null).concat(
      Array(daysInMonth).fill(null).map((_, i) => new Date(y, m, i + 1))
    );
  };

  const isDisabled = (d) => d < new Date(new Date().setHours(0,0,0,0));
  const isArrival = (d) => arrival && d.toDateString() === arrival.toDateString();
  const isDeparture = (d) => departure && d.toDateString() === departure.toDateString();
  const isInRange = (d) => arrival && departure && d > arrival && d < departure;

  const handleClick = (d) => {
    if (!arrival || (arrival && departure)) {
      onChange({ arrival: d, departure: null });
    } else {
      if (d < arrival) onChange({ arrival: d, departure: null });
      else onChange({ arrival, departure: d });
    }
  };

  const renderDays = (y, m) => renderMonth(y, m).map((d, i) => {
    if (!d) return <div key={`e${i}`} />;
    const disabled = isDisabled(d);
    const sel = isArrival(d) || isDeparture(d);
    const inRange = isInRange(d);
    return (
      <button key={i} onClick={() => !disabled && handleClick(d)} style={{
        padding: '6px 0', border: 'none', borderRadius: 6,
        fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        background: sel ? 'var(--green)' : inRange ? 'var(--green-light)' : 'transparent',
        color: disabled ? '#ccc' : sel ? '#fff' : 'var(--ink)',
        fontFamily: 'var(--sans)', fontWeight: sel ? 500 : 400,
      }}>
        {d.getDate()}
      </button>
    );
  });

  const months = isMobile
    ? [{ y: year, m: monthIdx }]
    : [{ y: year, m: monthIdx }, { y: nextMonth.getFullYear(), m: nextMonth.getMonth() }];

  return (
    <div style={{ display: 'flex', gap: 16, padding: '12px 16px', flexWrap: 'wrap' }}>
      {months.map(({ y, m }, ci) => (
        <div key={ci} style={{ width: isMobile ? '100%' : 220 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            {ci === 0
              ? <button onClick={() => setMonth(new Date(year, monthIdx - 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', padding: '4px 8px' }}>←</button>
              : <div style={{ width: 32 }} />}
            <span style={{ fontSize: 13, fontWeight: 500 }}>{MONTHS[m]} {y}</span>
            {ci === months.length - 1
              ? <button onClick={() => setMonth(new Date(year, monthIdx + 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', padding: '4px 8px' }}>→</button>
              : <div style={{ width: 32 }} />}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {['L','M','M','J','V','S','D'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, color: 'var(--ink-4)', padding: '3px 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {renderDays(y, m)}
          </div>
        </div>
      ))}
    </div>
  );
}

const formatDate = (d) => d ? `${d.getDate()} ${MONTHS[d.getMonth()].slice(0,3)}.` : null;

export default function SearchBar({ onSearch, compact = false }) {
  const [dest, setDest] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [dates, setDates] = useState({ arrival: null, departure: null });
  const [guests, setGuests] = useState(1);
  const [children, setChildren] = useState(0);
  const [openField, setOpenField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const ref = useRef(null);

  useNominatim(dest, setSuggestions);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpenField(null);
        setSuggestions([]);
      }
    };
    const onResize = () => setIsMobile(window.innerWidth < 768);
    document.addEventListener('mousedown', handler);
    window.addEventListener('resize', onResize);
    return () => { document.removeEventListener('mousedown', handler); window.removeEventListener('resize', onResize); };
  }, []);

  const handleSelectCity = useCallback((s) => {
    setDest(s.cityName);
    setSuggestions([]);
    localStorage.setItem('havenly_search_location', JSON.stringify({ cityName: s.cityName, lat: s.lat, lng: s.lng }));
    setOpenField('dates');
  }, []);

  const datesLabel = dates.arrival && dates.departure
    ? `${formatDate(dates.arrival)} → ${formatDate(dates.departure)}`
    : dates.arrival ? `${formatDate(dates.arrival)} → ?` : null;

  const guestsLabel = `${guests} adulte${guests > 1 ? 's' : ''}${children > 0 ? `, ${children} enfant${children > 1 ? 's' : ''}` : ''}`;

  const handleSearch = () => {
    setOpenField(null);
    setSuggestions([]);
    const saved = localStorage.getItem('havenly_search_location');
    if (saved) {
      try {
        const loc = JSON.parse(saved);
        if (loc.cityName !== dest) localStorage.removeItem('havenly_search_location');
      } catch(e) {
        localStorage.removeItem('havenly_search_location');
      }
    }
    localStorage.setItem('havenly_search_guests', JSON.stringify({ adults: guests, children }));
    onSearch && onSearch(dest, { adults: guests, children });
  };

  // MOBILE COMPACT
  if (isMobile && compact) {
    return (
      <div ref={ref} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, position: 'relative', zIndex: 99999 }}>
        <div style={{ flex: 1, background: 'var(--white)', border: '1px solid #DDD', borderRadius: 24, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => setOpenField(openField === 'dest' ? null : 'dest')}>
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><circle cx="8" cy="8" r="5.5" stroke="var(--ink-3)" strokeWidth="2"/><line x1="12.5" y1="12.5" x2="16" y2="16" stroke="var(--ink-3)" strokeWidth="2" strokeLinecap="round"/></svg>
          <span style={{ fontSize: 13, color: dest ? 'var(--ink)' : 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dest || 'Destination, dates...'}
          </span>
          {dest && (
            <button onClick={e => { e.stopPropagation(); setDest(''); setSuggestions([]); localStorage.removeItem('havenly_search_location'); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 16, lineHeight: 1, padding: 0, marginLeft: 'auto', flexShrink: 0 }}>×</button>
          )}
        </div>
        {openField === 'dest' && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 99999 }}>
            <input autoFocus
              style={{ width: '100%', padding: '12px 16px', border: 'none', borderBottom: suggestions.length ? '1px solid var(--border)' : 'none', borderRadius: 12, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', boxSizing: 'border-box' }}
              placeholder="Rechercher une destination..."
              value={dest}
              onChange={e => { setDest(e.target.value); localStorage.removeItem('havenly_search_location'); }}
            />
            {suggestions.map((s, i) => (
              <div key={i} onClick={() => { handleSelectCity(s); setOpenField(null); onSearch && onSearch(s.cityName); }}
                style={{ padding: '10px 16px', fontSize: 14, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {s.cityName}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // MOBILE COMPLET
  if (isMobile) {
    return (
      <div ref={ref} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 99999 }}>

        <div style={{ background: 'var(--white)', border: '1px solid #DDD', borderRadius: 12, padding: '10px 16px', position: 'relative', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>Destination</div>
          <input
            style={{ fontSize: 14, color: 'var(--ink)', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--sans)', width: '100%' }}
            placeholder="Rechercher une destination"
            value={dest}
            onChange={e => { setDest(e.target.value); localStorage.removeItem('havenly_search_location'); setOpenField('dest'); }}
            onFocus={() => setOpenField('dest')}
          />
          {openField === 'dest' && suggestions.length > 0 && (
            <CitySuggestions suggestions={suggestions} onSelect={handleSelectCity} />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div onClick={() => setOpenField(openField === 'dates' ? null : 'dates')}
            style={{ background: 'var(--white)', border: '1px solid #DDD', borderRadius: 12, padding: '10px 16px', cursor: 'pointer', position: 'relative' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>Dates</div>
            <div style={{ fontSize: 13, color: datesLabel ? 'var(--ink)' : 'var(--ink-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {datesLabel || 'Quand ?'}
            </div>
          </div>
          <div onClick={() => setOpenField(openField === 'guests' ? null : 'guests')}
            style={{ background: 'var(--white)', border: '1px solid #DDD', borderRadius: 12, padding: '10px 16px', cursor: 'pointer', position: 'relative' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>Voyageurs</div>
            <div style={{ fontSize: 13, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{guestsLabel}</div>
          </div>
        </div>

        {openField === 'guests' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999998, display: 'flex', alignItems: 'flex-end' }}
            onClick={() => setOpenField(null)}>
            <div style={{ background: 'var(--white)', borderRadius: '20px 20px 0 0', width: '100%', padding: '24px 24px 40px' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <span style={{ fontSize: 16, fontWeight: 500 }}>Voyageurs</span>
                <button onClick={() => setOpenField(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>Adultes</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>13 ans et plus</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button onClick={() => setGuests(g => Math.max(1, g - 1))} style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--border)', background: guests <= 1 ? 'var(--surface)' : 'var(--white)', cursor: guests <= 1 ? 'default' : 'pointer', fontSize: 20, color: guests <= 1 ? 'var(--ink-4)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 500, minWidth: 24, textAlign: 'center' }}>{guests}</span>
                  <button onClick={() => setGuests(g => Math.min(16, g + 1))} style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 20, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 20, borderTop: '1px solid var(--border)', marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>Enfants</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>2 à 12 ans</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button onClick={() => setChildren(c => Math.max(0, c - 1))} style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--border)', background: children <= 0 ? 'var(--surface)' : 'var(--white)', cursor: children <= 0 ? 'default' : 'pointer', fontSize: 20, color: children <= 0 ? 'var(--ink-4)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 500, minWidth: 24, textAlign: 'center' }}>{children}</span>
                  <button onClick={() => setChildren(c => Math.min(10, c + 1))} style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 20, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <button onClick={() => setOpenField(null)} style={{ width: '100%', padding: '14px', background: 'var(--green)', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                Confirmer — {guestsLabel}
              </button>
            </div>
          </div>
        )}

        {openField === 'dates' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={() => setOpenField(null)}>
            <div style={{ background: 'var(--white)', borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', width: '100%', maxWidth: 380 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>Sélectionnez vos dates</span>
                <button onClick={() => setOpenField(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
              </div>
              <CalendarDouble arrival={dates.arrival} departure={dates.departure} isMobile={true}
                onChange={(d) => { setDates(d); if (d.arrival && d.departure) setOpenField(null); }} />
              {dates.arrival && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
                  {dates.departure
                    ? `${Math.round((dates.departure - dates.arrival) / (1000*60*60*24))} nuits sélectionnées ✓`
                    : 'Sélectionnez la date de départ'}
                </div>
              )}
            </div>
          </div>
        )}

        <button onClick={handleSearch}
          style={{ width: '100%', padding: '13px', background: 'var(--green)', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="8" cy="8" r="5.5" stroke="#fff" strokeWidth="2"/><line x1="12.5" y1="12.5" x2="16" y2="16" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
          Rechercher
        </button>
      </div>
    );
  }

  // DESKTOP
  const barStyle = {
    display: 'flex', alignItems: 'center',
    background: 'var(--white)',
    border: '1px solid #DDDDDD',
    borderRadius: compact ? 12 : 48,
    minWidth: compact ? 'auto' : 600,
    boxShadow: compact ? '0 1px 6px rgba(0,0,0,0.08)' : '0 2px 16px rgba(0,0,0,0.12)',
    maxWidth: compact ? '100%' : 860,
    width: '100%', height: compact ? 58 : 66,
    position: 'relative', zIndex: 99999,
  };

  const fieldStyle = (flex, hasRight) => ({
    display: 'flex', flexDirection: 'column',
    padding: compact ? '8px 16px' : '10px 24px',
    flex, borderRight: hasRight ? '1px solid #DDDDDD' : 'none',
    minWidth: 0, cursor: 'pointer', height: '100%',
    justifyContent: 'center', position: 'relative', overflow: 'visible',
  });

  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--ink)', marginBottom: 3, letterSpacing: '0.1px', whiteSpace: 'nowrap' };
  const placeholderStyle = (val) => ({ fontSize: 14, color: val ? 'var(--ink)' : 'var(--ink-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' });
  const dropdownStyle = { position: 'absolute', top: 'calc(100% + 8px)', left: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 99999 };

  return (
    <div style={barStyle} ref={ref}>

      {/* Destination */}
      <div style={fieldStyle(2, true)} onClick={() => setOpenField('dest')}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span style={labelStyle}>Destination</span>
          <input
            style={{ fontSize: 14, color: 'var(--ink)', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--sans)', width: '100%', padding: 0, margin: 0, lineHeight: '20px', height: 20 }}
            placeholder="Rechercher une destination"
            value={dest}
            onChange={e => { setDest(e.target.value); localStorage.removeItem('havenly_search_location'); setOpenField('dest'); }}
            onFocus={() => setOpenField('dest')}
          />
        </div>
        {openField === 'dest' && suggestions.length > 0 && (
          <CitySuggestions suggestions={suggestions} onSelect={handleSelectCity} style={{ minWidth: 300 }} />
        )}
      </div>

      {/* Dates */}
      <div style={fieldStyle(1.5, true)} onClick={() => setOpenField(openField === 'dates' ? null : 'dates')}>
        <span style={labelStyle}>Dates</span>
        <span style={placeholderStyle(datesLabel)}>{datesLabel || 'Quand ?'}</span>
        {openField === 'dates' && (
          <div style={{ ...dropdownStyle, left: '50%', transform: 'translateX(-50%)' }} onClick={e => e.stopPropagation()}>
            <CalendarDouble arrival={dates.arrival} departure={dates.departure} isMobile={false}
              onChange={(d) => { setDates(d); if (d.arrival && d.departure) setOpenField(null); }} />
            {dates.arrival && (
              <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
                {dates.departure ? `${Math.round((dates.departure - dates.arrival) / (1000*60*60*24))} nuits sélectionnées` : 'Sélectionnez la date de départ'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Voyageurs desktop */}
      <div style={fieldStyle(1, false)} onClick={() => setOpenField(openField === 'guests' ? null : 'guests')}>
        <span style={labelStyle}>Voyageurs</span>
        <span style={placeholderStyle(guests > 0)}>{guestsLabel}</span>
        {openField === 'guests' && (
          <div style={{ ...dropdownStyle, right: 0, left: 'auto', minWidth: 260 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Adultes</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>13 ans et plus</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setGuests(g => Math.max(1, g - 1))} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: guests <= 1 ? 'var(--surface)' : 'var(--white)', cursor: guests <= 1 ? 'default' : 'pointer', fontSize: 18, color: guests <= 1 ? 'var(--ink-4)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 16, fontWeight: 500, minWidth: 20, textAlign: 'center' }}>{guests}</span>
                  <button onClick={() => setGuests(g => Math.min(16, g + 1))} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 18, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Enfants</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>2 à 12 ans</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setChildren(c => Math.max(0, c - 1))} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: children <= 0 ? 'var(--surface)' : 'var(--white)', cursor: children <= 0 ? 'default' : 'pointer', fontSize: 18, color: children <= 0 ? 'var(--ink-4)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 16, fontWeight: 500, minWidth: 20, textAlign: 'center' }}>{children}</span>
                  <button onClick={() => setChildren(c => Math.min(10, c + 1))} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 18, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bouton recherche */}
      <button style={{ flexShrink: 0, width: 48, height: 48, margin: '0 9px', borderRadius: '50%', background: 'var(--green)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        onClick={handleSearch}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="8" cy="8" r="5.5" stroke="#fff" strokeWidth="2"/>
          <line x1="12.5" y1="12.5" x2="16" y2="16" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}
